/**
 * ConfigRegistry.jsx
 *
 * ONE generic CRUD component that renders ANY governed config taxonomy from its
 * declarative entry in lib/configRegistry.js. Replaces the three near-identical
 * bespoke sections (Severity / Status / Treatment) in AdminPanel.jsx.
 *
 * Mental model for the admin: a section is always a DRAFT you edit inline, then
 * PUBLISH. Nothing is persisted until you publish, and publishing always shows a
 * diff of exactly what will change. An audit trail of past publishes is one
 * click away.
 *
 * Mental model for the maintainer: this file never names a specific config type.
 * Behaviour comes entirely from the schema. Add a type → add a schema entry.
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  REGISTRY, validateItems, diffItems, fmtVal,
} from '../lib/configRegistry.js';
import {
  IntegrityVerifier,
  AuditPanel,
  PublishConfirmModal,
} from './governedConfigChrome.jsx';
import {
  Badge, SwatchPicker, EventTypeAvailability, EventTypePills,
  TreatmentTypeAvailability, TreatmentTypePills,
} from './configControls.jsx';

const clone = (x) => JSON.parse(JSON.stringify(x ?? []));
const bySort = (a, b) => (a.order ?? 0) - (b.order ?? 0);

// ── Field editor — dispatches on field.type ─────────────────────────────────────
function FieldEditor({ spec, field, value, onChange }) {
  switch (field.type) {
    case 'colour':
      return (
        <div className="field" style={{ marginBottom: 0 }}>
          <label style={{ fontSize: 11 }}>{field.label}</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
            <SwatchPicker value={value || ''} onChange={onChange} />
            <Badge cssClass={value || ''} label="Preview" />
          </div>
        </div>
      );
    case 'bool':
      return (
        <div className="field" style={{ flex: 1, marginBottom: 0 }}>
          <label style={{ fontSize: 11 }}>{field.label}</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 13 }}>
            <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} />
            {field.boolLabel || 'Enabled'}
          </label>
        </div>
      );
    case 'eventTypes':
      return <EventTypeAvailability value={value} onChange={onChange} />;
    case 'treatmentTypes':
      return <TreatmentTypeAvailability value={value} onChange={onChange} />;
    case 'aliases':
      return (
        <div className="field" style={{ marginBottom: 0 }}>
          <label style={{ fontSize: 11 }}>{field.label}</label>
          <input className="input" style={{ fontSize: 13 }}
            value={(value || []).join(', ')}
            onChange={e => onChange(e.target.value.split(',').map(a => a.trim().toUpperCase()).filter(Boolean))} />
          {field.hint && <div className="field-hint" style={{ fontSize: 11 }}>{field.hint}</div>}
        </div>
      );
    case 'number':
      return (
        <div className="field" style={{ width: 120, marginBottom: 0 }}>
          <label style={{ fontSize: 11 }}>{field.label}</label>
          <input className="input" type="number" style={{ fontSize: 13 }}
            value={value ?? 0} onChange={e => onChange(Number(e.target.value) || 0)} />
        </div>
      );
    default: // text
      return (
        <div className="field" style={{ flex: field.editorWidth === 'flex' ? 1 : undefined, marginBottom: 0 }}>
          <label style={{ fontSize: 11 }}>{field.label}</label>
          <input className="input" style={{ fontSize: 13 }}
            value={value ?? ''} onChange={e => onChange(e.target.value)} />
        </div>
      );
  }
}

// ── Inline row editor ───────────────────────────────────────────────────────────
function RowEditor({ spec, draft, rowErrors, onField, onApply, onCancel }) {
  // For label/value-coupled taxonomies, editing label drives value.
  const setField = (name, val) => {
    if (name === 'label' && spec.syncValueFromLabel) onField({ label: val, value: val });
    else onField({ [name]: val });
  };
  return (
    <div style={{
      background: 'var(--surface-2)', border: '1px solid var(--brand)', borderRadius: 10,
      padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 12,
    }}>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {spec.fields.filter(f => f.editorWidth === 'flex' || f.type === 'bool').map(f => (
          <FieldEditor key={f.name} spec={spec} field={f} value={draft[f.name]} onChange={v => setField(f.name, v)} />
        ))}
      </div>
      {spec.fields.filter(f => f.editorWidth !== 'flex' && f.type !== 'bool').map(f => (
        <FieldEditor key={f.name} spec={spec} field={f} value={draft[f.name]} onChange={v => setField(f.name, v)} />
      ))}
      {rowErrors?.length > 0 && (
        <div style={{ fontSize: 11, color: 'var(--danger)' }}>{rowErrors.join(' ')}</div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn primary sm" onClick={onApply} style={{ fontSize: 12 }}>Apply</button>
        <button className="btn sm" onClick={onCancel} style={{ fontSize: 12 }}>Cancel</button>
      </div>
    </div>
  );
}

// ── Read-only row ───────────────────────────────────────────────────────────────
// Reordering is drag-and-drop (native HTML5 DnD) with a dedicated grab handle.
// The handle is keyboard accessible: focus it and press ArrowUp / ArrowDown to
// move the row one slot (same renumbering as a drop). Either path renumbers
// `order` 1..N on the draft.
function RowView({
  spec, item, position, total, hasError, isDragging, isDropTarget,
  onEdit, onRemove, onMoveBy, onDragStart, onDragOver, onDrop, onDragEnd,
}) {
  function handleKey(e) {
    if (e.key === 'ArrowUp')        { e.preventDefault(); onMoveBy(-1); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); onMoveBy(+1); }
  }
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '8px 14px', borderRadius: 10,
        border: `1px solid ${hasError ? 'var(--danger)' : isDropTarget ? 'var(--brand)' : 'var(--border)'}`,
        background: 'var(--surface)',
        opacity: isDragging ? 0.4 : 1,
        boxShadow: isDropTarget ? '0 0 0 2px var(--brand) inset' : 'none',
      }}>
      {/* Drag handle — also keyboard reorderable via Arrow keys. */}
      <span
        role="button"
        tabIndex={0}
        aria-label={`Reorder ${item.label}. Position ${position} of ${total}. Use arrow up and arrow down keys to move.`}
        title="Drag to reorder (or focus and use ↑/↓)"
        onKeyDown={handleKey}
        style={{
          cursor: 'grab', userSelect: 'none', color: 'var(--text-3)',
          fontSize: 14, lineHeight: 1, padding: '4px 4px', borderRadius: 6,
        }}>⠿</span>
      <span style={{ fontSize: 10, color: 'var(--text-3)', width: 16, textAlign: 'right' }}>{position}</span>
      <Badge cssClass={item[spec.rowBadgeField] || ''} label={item.label} />
      <EventTypePills types={item.eventTypes} />
      <TreatmentTypePills types={item.treatmentTypes} />
      <span style={{ fontSize: 12, color: 'var(--text-3)', flex: 1 }}>{spec.summary(item)}</span>
      {hasError && (
        <span title="This row has validation errors" style={{ fontSize: 11, color: 'var(--danger)', fontWeight: 600 }}>!</span>
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        <button className="btn ghost sm" onClick={onEdit} style={{ fontSize: 11 }}>Edit</button>
        <button className="btn ghost sm" onClick={onRemove} style={{ fontSize: 11, color: 'var(--danger)' }}>Remove</button>
      </div>
    </div>
  );
}

// ── Publish diff preview ────────────────────────────────────────────────────────
function DiffPreview({ diff }) {
  const Line = ({ tag, color, children }) => (
    <li style={{ fontSize: 12, lineHeight: 1.6 }}>
      <span style={{ fontWeight: 700, color, marginRight: 6 }}>{tag}</span>{children}
    </li>
  );
  return (
    <ul style={{ margin: '8px 0 0', paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {diff.added.map(i => <Line key={`a-${i.key}`} tag="ADD" color="var(--ok)"><strong>{i.label}</strong></Line>)}
      {diff.removed.map(i => <Line key={`r-${i.key}`} tag="REMOVE" color="var(--danger)"><strong>{i.label}</strong></Line>)}
      {diff.changed.map(c => (
        <Line key={`c-${c.item.key}`} tag="EDIT" color="var(--brand)">
          <strong>{c.item.label}</strong>{' — '}
          {c.diffs.map((d, i) => (
            <span key={d.field}>
              {i > 0 && ', '}
              {d.field}: <code>{fmtVal(d.from)}</code> → <code>{fmtVal(d.to)}</code>
            </span>
          ))}
        </Line>
      ))}
    </ul>
  );
}

// ── The generic section ─────────────────────────────────────────────────────────
export default function ConfigRegistry({ configKey, items: published, onPublish }) {
  const spec = REGISTRY[configKey];
  if (!spec) return null;

  const [items, setItems]       = useState(() => clone(published));
  const [editKey, setEditKey]   = useState(null);
  const [draft, setDraft]       = useState(null);
  const [dirty, setDirty]       = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showAudit, setShowAudit]   = useState(false);
  const [auditToken, setAuditToken] = useState(0);
  const [dragId, setDragId]         = useState(null);   // id being dragged
  const [overId, setOverId]         = useState(null);   // id currently hovered

  // Resync from server config when it loads/changes — but never clobber an
  // in-progress draft (matches the IncidentCategories pattern).
  useEffect(() => { if (!dirty) setItems(clone(published)); }, [published]); // eslint-disable-line react-hooks/exhaustive-deps

  const validation = useMemo(() => validateItems(configKey, items), [configKey, items]);
  const diff       = useMemo(() => diffItems(configKey, published, items), [configKey, published, items]);
  const sorted     = useMemo(() => [...items].sort(bySort), [items]);

  function startEdit(item) { setEditKey(item[spec.idField]); setDraft(clone(item)); }
  function cancelEdit()    { setEditKey(null); setDraft(null); }
  function applyEdit() {
    setItems(prev => prev.map(i => i[spec.idField] === editKey ? draft : i));
    setDirty(true); cancelEdit();
  }
  function addItem() {
    const item = spec.newItem(items);
    setItems(prev => [...prev, item]);
    setDirty(true);
    startEdit(item);
  }
  function remove(id) {
    setItems(prev => prev.filter(i => i[spec.idField] !== id));
    setDirty(true);
    if (editKey === id) cancelEdit();
  }
  // Move the row with `id` to absolute target index in sort order, then renumber
  // `order` 1..N so the new sequence is explicit and persists on publish. Both
  // drag-drop and keyboard reordering funnel through here.
  function moveTo(id, targetIdx) {
    setItems(prev => {
      const ordered = [...prev].sort(bySort);
      const from = ordered.findIndex(i => i[spec.idField] === id);
      if (from < 0) return prev;
      const to = Math.max(0, Math.min(ordered.length - 1, targetIdx));
      if (to === from) return prev;
      const [moved] = ordered.splice(from, 1);
      ordered.splice(to, 0, moved);
      return ordered.map((it, i) => ({ ...it, order: i + 1 }));
    });
    setDirty(true);
  }
  // Relative move (keyboard ↑/↓): shift one slot from current position.
  function moveBy(id, dir) {
    const ordered = [...items].sort(bySort);
    const from = ordered.findIndex(i => i[spec.idField] === id);
    if (from < 0) return;
    moveTo(id, from + dir);
  }
  function discard() {
    setItems(clone(published)); setDirty(false); setPreviewing(false); cancelEdit();
  }
  async function publish() {
    setPublishing(true);
    try {
      await onPublish(configKey, items, { publish: true });
      setDirty(false); setPreviewing(false);
      setAuditToken(t => t + 1);  // refresh audit list after a successful publish
    } finally {
      setPublishing(false);
    }
  }

  const rowErr = validation.byId;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            {spec.title}
            {dirty && <span className="badge" style={{ marginLeft: 8, fontSize: 10 }}>Draft</span>}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{spec.description}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button className="btn ghost sm" onClick={() => setShowAudit(s => !s)} style={{ fontSize: 11 }}>
            {showAudit ? 'Hide history' : 'History'}
          </button>
          <button className="btn" onClick={addItem} style={{ fontSize: 12 }}>+ Add {spec.singular}</button>
          {dirty && (
            <button className="btn primary" onClick={() => setPreviewing(true)} disabled={!validation.ok}
              title={validation.ok ? 'Review and publish' : 'Fix validation errors first'} style={{ fontSize: 12 }}>
              Review &amp; publish
            </button>
          )}
        </div>
      </div>

      {!validation.ok && (
        <div className="callout danger" style={{ marginBottom: 12 }}>
          <div style={{ flex: 1, fontSize: 12 }}>
            <strong>Cannot publish yet:</strong>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {validation.errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          </div>
        </div>
      )}

      {showAudit && (
        <div style={{ marginBottom: 14, padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface-2)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Change history</div>
          <IntegrityVerifier />
          <AuditPanel configKey={configKey} refreshToken={auditToken} />
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {sorted.map((item, i) => {
          const id = item[spec.idField];
          return (
            <div key={id}>
              {editKey === id && draft ? (
                <RowEditor
                  spec={spec} draft={draft} rowErrors={rowErr[id]}
                  onField={(patch) => setDraft(d => ({ ...d, ...patch }))}
                  onApply={applyEdit} onCancel={cancelEdit}
                />
              ) : (
                <RowView
                  spec={spec} item={item} hasError={!!rowErr[id]?.length}
                  position={i + 1} total={sorted.length}
                  isDragging={dragId === id}
                  isDropTarget={overId === id && dragId !== null && dragId !== id}
                  onEdit={() => startEdit(item)} onRemove={() => remove(id)}
                  onMoveBy={(dir) => moveBy(id, dir)}
                  onDragStart={(e) => { setDragId(id); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', String(id)); } catch { /* jsdom */ } }}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (overId !== id) setOverId(id); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragId != null && dragId !== id) moveTo(dragId, i);
                    setDragId(null); setOverId(null);
                  }}
                  onDragEnd={() => { setDragId(null); setOverId(null); }}
                />
              )}
            </div>
          );
        })}
      </div>

      <PublishConfirmModal
        open={previewing}
        title={`Publish ${spec.title}?`}
        description="These changes affect how every adverse event is classified. Review the diff before publishing."
        publishing={publishing}
        hasChanges={diff.hasChanges}
        canPublish={validation.ok}
        onCancel={() => setPreviewing(false)}
        onDiscard={discard}
        onPublish={publish}
      >
        <DiffPreview diff={diff} />
      </PublishConfirmModal>
    </div>
  );
}
